import React, { useMemo, useState } from 'react';
import { observer, inject } from 'mobx-react';
import classnames from 'classnames';

import withUserContext from 'behaviors/withUserContext';

import Currency from 'components/Currency/Currency';
import CauzeSpinner from 'components/CauzeSpinner/CauzeSpinner';
import AmountSelector from 'components/AmountSelector/AmountSelectorMini';
import useCheckoutStore from 'stores/CheckoutStoreV2';
import Avatar from 'components/Avatar/Avatar';

const FundCharityModal = ({
  isOpen,
  onToggleClose,
  activeEntity,
  charityStore,
  addFundsGiftCheckoutStore,
  onSuccess = (_amt) => {},
  onCancel = () => {},
  defaultAmount = 10000,
  charity,
}) => {
  const checkoutStore = useCheckoutStore();
  const [donationAmount, setDonationAmount] = useState(defaultAmount);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const onToggleCancel = () => {
    onCancel();
    onToggleClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (donationAmount === 0) {
      addFundsGiftCheckoutStore.popErrorModal('Please select an amount.');
      return;
    }

    setLoading(true);

    try {
      await checkoutStore.fundCharityFromBalance({
        amount: donationAmount,
        charityId: charity.id,
        comment,
      });

      const charityData = charityStore.charities.get(+charity.id);

      if (charityData) {
        charityStore.charities.set(+charity.id, {
          ...charityData,
          donationAmount: (charityData.donationAmount || 0) + donationAmount,
        });
      }

      onSuccess?.(donationAmount);
      onToggleClose();
    } catch (error) {
      addFundsGiftCheckoutStore.popErrorModal(
        'There was an issue processing your donation. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const hasBalanceError = useMemo(() => {
    if (activeEntity?.balance?.allowNegative) return false;

    return donationAmount > (activeEntity?.balance?.total ?? 0);
  }, [donationAmount, activeEntity]);

  return (
    <div
      className={classnames('fund-charity-modal modal', {
        'is-active': isOpen,
      })}
    >
      <div onClick={onToggleCancel} className="modal-background"></div>
      <div className="modal-card min-h-[80vh] max-w-96">
        <header className="modal-card-head !bg-white">
          <p className="modal-card-title text-center">Fund Nonprofit</p>
          <button
            className="delete"
            aria-label="close"
            onClick={onToggleCancel}
          ></button>
        </header>
        {loading ? (
          <section className="modal-card-body flex flex-col justify-center">
            <CauzeSpinner />
          </section>
        ) : (
          <section className="modal-card-body flex flex-col gap-y-4">
            <div className="form-container !flex-grow-0">
              <div className="flex gap-x-4">
                <Avatar
                  noBorder
                  md
                  entityType="CHARITY"
                  avatar={charity?.avatar}
                />
                <div>
                  <div className="font-agenda-bold">{charity?.name}</div>
                  <div className="text-[14px] text-gray-3d3 leading-tight">
                    You will be granting money from your balance to this
                    nonprofit/project.
                  </div>
                </div>
              </div>
            </div>
            <div className="form-container !flex-grow-0">
              <div className="flex-column flex-center">
                <div className="text-left w-full text-bold">Amount:</div>
                <AmountSelector
                  amounts={[10000, 50000, 100000]}
                  defaultAmount={defaultAmount}
                  selectedAmount={donationAmount}
                  onAmountUpdate={(amt) => setDonationAmount(amt)}
                />
              </div>
            </div>
            <div className="form-container !flex-grow-0">
              <div className="flex-column flex-center">
                <div className="text-bold w-full text-left">Note:</div>
                <textarea
                  className={
                    'textarea email-list-input !border-black border-solid focus:border-none'
                  }
                  maxRows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a note to your donation..."
                />
              </div>
            </div>
            <div className="checkout-summary py-2 mt-auto border-0 border-t-2 border-solid border-lightgray-b2b">
              <div className="flex flex-col">
                <div className="line-item flex-row">
                  <div>Current Balance:</div>
                  <div>
                    <Currency
                      showCents
                      amount={activeEntity?.balance?.total ?? 0}
                    />
                  </div>
                </div>
                <div className="line-item flex-row">
                  <div>Your Donation: </div>
                  <div>
                    <Currency showCents amount={donationAmount} />
                  </div>
                </div>
                <div
                  className={classnames('line-item flex-row', {
                    'text-red-600': hasBalanceError,
                  })}
                >
                  <div>Remaining Balance: </div>
                  <div>
                    <Currency
                      showCents
                      amount={
                        (activeEntity?.balance?.total ?? 0) - donationAmount
                      }
                    />
                  </div>
                </div>
                <div className="line-item total">
                  <div className="font-agenda-bold h-4">Total Due</div>
                  <div className="line-item-amount">
                    <Currency showCents amount={donationAmount} />
                  </div>
                </div>
                {hasBalanceError && (
                  <div className="text-agenda-bold pt-4 text-red-600">
                    * Insufficient Balance
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
        <footer className="modal-card-foot !bg-white !border-none !pt-0">
          <button
            className="cz-btn-brand flex-1"
            onClick={handleSubmit}
            disabled={hasBalanceError || !donationAmount || loading}
          >
            Confirm Donation
          </button>
        </footer>
      </div>
    </div>
  );
};

export default withUserContext(
  inject(
    'profileStore',
    'authStore',
    'uiStore',
    'charityStore',
    'addFundsGiftCheckoutStore',
  )(observer(FundCharityModal)),
);
